import { useState, useEffect, useMemo } from 'react'
import { FileItem } from '@/types'
import apiService from '@/services/api'

interface UseFileDataReturn {
  fileText: string | null
  isLoadingText: boolean
  tags: string[]
  isLoadingTags: boolean
  error: string | null
  refreshData: () => void
}

export function useFileData(file?: FileItem | null): UseFileDataReturn {
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Extract file text directly from the file object
  const fileText = useMemo(() => {
    if (!file) return null

    // Use extracted_text if available and OCR is completed
    if (file.ocr_status === 'completed' && file.extracted_text) {
      return file.extracted_text
    }

    return null
  }, [file?.extracted_text, file?.ocr_status])

  // Extract tags directly from the file object
  const tags = useMemo(() => {
    if (!file?.tags) return []

    // Convert FileTag objects to string array
    return file.tags.map((tag) => tag.tag)
  }, [file?.tags])

  // Only load data if it's missing from the file object
  const loadMissingData = async () => {
    if (!file) return

    try {
      setError(null)
      setIsRefreshing(true)

      // Only load text if OCR is completed but extracted_text is missing
      if (file.ocr_status === 'completed' && !file.extracted_text) {
        await apiService.getFileText(file.id)
      }

      // Only load tags if tags array is missing
      if (!file.tags) {
        await apiService.getFileTags(file.id)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load missing file data')
    } finally {
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    if (file) {
      // Reset error state
      setError(null)

      // Only load data if it's actually missing
      const needsText = file.ocr_status === 'completed' && !file.extracted_text
      const needsTags = !file.tags

      if (needsText || needsTags) {
        loadMissingData()
      }
    }
  }, [file?.id]) // Only re-run when file ID changes

  const refreshData = () => {
    if (file) {
      loadMissingData()
    }
  }

  return {
    fileText,
    isLoadingText: isRefreshing && file?.ocr_status === 'completed' && !file?.extracted_text,
    tags,
    isLoadingTags: isRefreshing && !file?.tags,
    error,
    refreshData,
  }
}
